import { FC, memo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Contract } from 'entities/Contract';
import { Button } from 'shared/ui/Button';
import { Modal } from 'shared/ui/Modal';
import { getPriceRaiseAcceptDetails, PriceRaiseAcceptDetailsExtras } from '../utils/getPriceRaiseAcceptDetails';

interface AcceptPriceRaiseModalProps {
  isOpened: boolean;
  contract: Nullable<Contract>;
  proposedPrice: Nullable<number>;
  currencySymbol: string;
  extras?: PriceRaiseAcceptDetailsExtras;
  isLoading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export const AcceptPriceRaiseModal: FC<AcceptPriceRaiseModalProps> = memo((props) => {
  const { isOpened, contract, proposedPrice, currencySymbol, extras, isLoading, onCancel, onConfirm } = props;

  const { t } = useAppTranslation(['contracts', 'common']);

  const details = contract && proposedPrice != null ? getPriceRaiseAcceptDetails(contract, proposedPrice, currencySymbol, t, extras) : [];

  return (
    <Modal isOpen={isOpened} onClose={onCancel} width={480} zIndex={1200}>
      <div className="font-semibold text-xl mb-3">{t('Accept price raise')}</div>
      {contract && (
        <div className="text-primaryLight font-normal mb-6">
          <div className="mb-3">
            {t('Are you sure you want to accept this price raise for contract {{contractNumber}}?', {
              contractNumber: contract.contractNumber,
            })}
          </div>
          <div className="space-y-2">
            {details.map(({ label, value }) => (
              <div key={label} className="flex justify-between gap-4">
                <span>{label}</span>
                <span className="text-primary font-medium text-right whitespace-pre-line">{value || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-3">
        <Button theme="secondary" onClick={onCancel}>
          {t('Cancel', { ns: 'common' })}
        </Button>
        <Button onClick={onConfirm} isLoading={isLoading} isDisabled={!contract || proposedPrice == null}>
          {t('Confirm', { ns: 'common' })}
        </Button>
      </div>
    </Modal>
  );
});
